import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { processApi } from "@/lib/processApi";
import type { AllowedActions } from "@/lib/aclActions";
import { useAuth } from "@/auth/AuthContext";
import { TicketHeader } from "@/components/agent/TicketHeader";
import { ArticleMasterDetail } from "@/components/agent/ArticleMasterDetail";
import { HistoryTable } from "@/components/agent/HistoryTable";
import { PluginTicketPanels } from "@/plugins/slots";
import { MajorIncidentBanner } from "@/components/itsm/MajorIncidentBanner";
import { MajorIncidentPanel } from "@/components/itsm/MajorIncidentPanel";
import { ProblemPanel } from "@/components/itsm/ProblemPanel";
import { TicketCisPanel } from "@/components/itsm/TicketCisPanel";
import { PresenceBar } from "@/components/agent/PresenceBar";
import { TicketApprovalsPanel } from "@/components/approvals/TicketApprovalsPanel";
import { ChangePanel } from "@/components/itsm/change/ChangePanel";
import { ProcessWidget } from "@/components/agent/process/ProcessWidget";
import { AiPanel } from "@/components/agent/AiPanel";
import { SimilarTicketsChip } from "@/components/agent/SimilarTicketsChip";
import { TicketZoomOverflowItems } from "@/components/agent/TicketZoomOverflowMenu";
import { Spinner } from "@/components/ui/Spinner";
import { recordTicketView } from "@/lib/lastViews";

// Sliding presence renewal: comfortably inside the backend's 30s TTL (see
// POST /api/v1/tickets/{id}/presence) so a normal heartbeat cadence never
// lets the entry expire while the agent is still on the page.
const PRESENCE_HEARTBEAT_MS = 20000;

export function TicketZoomPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { ticketId: ticketIdStr } = useParams({
    from: "/agent/tickets/$ticketId",
  });
  const ticketId = Number(ticketIdStr);
  const [tab, setTab] = useState<"articles" | "history">("articles");
  const [composing, setComposing] = useState(false);
  // Sort state lives here so the ⋮ menu can toggle it for either view.
  const [articleDescending, setArticleDescending] = useState(true);
  const [historyOrder, setHistoryOrder] = useState<"asc" | "desc">("desc");
  const [noteOpen, setNoteOpen] = useState(false);
  const [processStartOpen, setProcessStartOpen] = useState(false);
  const validTicketId = Number.isFinite(ticketId) && ticketId > 0;

  const ticketQ = useQuery({
    queryKey: ["tickets", ticketId],
    queryFn: () => api.getTicket(ticketId),
    enabled: validTicketId,
  });

  const processStateQ = useQuery({
    queryKey: ["process", "ticket", ticketId, "state"],
    queryFn: ({ signal }) => processApi.ticketState(ticketId, signal),
    enabled: validTicketId,
  });

  // Znuny's zoom menu runs the Action ACLs over its entries; a forbidden entry is hidden.
  // null (loading / error) = show everything.
  const aclActionsQ = useQuery({
    queryKey: ["tickets", ticketId, "acl-actions"],
    queryFn: ({ signal }) => processApi.aclActions(ticketId, signal),
    enabled: validTicketId,
  });
  const allowedActions: AllowedActions = aclActionsQ.data ? new Set(aclActionsQ.data.allowed) : null;

  // Remember recently viewed tickets for the dashboard "Last views" strip.
  useEffect(() => {
    if (!ticketQ.data) return;
    recordTicketView({
      id: ticketQ.data.id,
      tn: ticketQ.data.tn,
      title: ticketQ.data.title,
    });
  }, [ticketQ.data]);

  // Presence heartbeat: announce viewing/composing on mount, on mode
  // change, and on an interval well under the 30s server-side TTL.
  useEffect(() => {
    if (!validTicketId) return undefined;
    const mode = composing ? "composing" : "viewing";
    const post = () => {
      void api.postPresence(ticketId, { mode }).catch(() => {
        // Best-effort — presence is a nice-to-have, not a critical write.
      });
    };
    post();
    const interval = window.setInterval(post, PRESENCE_HEARTBEAT_MS);
    return () => window.clearInterval(interval);
  }, [ticketId, validTicketId, composing]);

  if (!validTicketId) {
    return <p className="p-6 text-sm text-danger">{t("ticket.invalidId")}</p>;
  }

  if (ticketQ.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (ticketQ.isError || !ticketQ.data) {
    return (
      <div className="p-6">
        <p className="text-danger">{t("ticket.loadError")}</p>
        <Link to="/agent/queues" className="mt-2 inline-block text-sm text-accent">
          {t("common.back")}
        </Link>
      </div>
    );
  }

  const canStartProcess =
    !processStateQ.isLoading &&
    !processStateQ.isError &&
    !processStateQ.data?.process_entity_id &&
    Boolean(processStateQ.data?.can_start);

  const sortLabel =
    tab === "articles"
      ? articleDescending
        ? t("ticket.sortNewestFirst")
        : t("ticket.sortOldestFirst")
      : historyOrder === "desc"
        ? t("ticket.historySortDesc")
        : t("ticket.historySortAsc");

  const onToggleSort = () => {
    if (tab === "articles") {
      setArticleDescending((d) => !d);
    } else {
      setHistoryOrder((o) => (o === "desc" ? "asc" : "desc"));
    }
  };

  // note permission: prefer explicit permissions.note; fall back to can_write.
  const canNote = ticketQ.data
    ? ticketQ.data.permissions
      ? Boolean(ticketQ.data.permissions.note || ticketQ.data.permissions.rw)
      : Boolean(ticketQ.data.can_write)
    : false;
  // Deleting a note is destructive — gate on ``rw`` specifically, not the
  // weaker ``note`` permission that's enough to create one.
  const canDeleteNote = Boolean(ticketQ.data?.permissions?.rw);

  const overflowItems = (
    <TicketZoomOverflowItems
      tab={tab}
      onTabChange={(next) => {
        setTab(next);
        // Opening history while composing a note: leave the note alone.
      }}
      sortLabel={sortLabel}
      onToggleSort={onToggleSort}
      onInternalNote={() => {
        setTab("articles");
        setNoteOpen(true);
      }}
      canNote={canNote}
      canStartProcess={Boolean(canStartProcess)}
      allowedActions={allowedActions}
      onStartProcess={() => setProcessStartOpen(true)}
    />
  );

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-4" data-testid="ticket-zoom">
      {/* Ticket info + primary actions; the AI panel hands its pieces to the
          header, which places them (summary under the title, drafts next to
          "Antworten", banners above the status bar). */}
      <AiPanel ticketId={ticketId} canNote={canNote}>
        {(ai) => (
          <TicketHeader
            ticket={ticketQ.data}
            overflowItems={overflowItems}
            canNote={canNote}
            allowedActions={allowedActions}
            onOpenNote={() => {
              setTab("articles");
              setNoteOpen(true);
            }}
            ai={ai}
            similar={<SimilarTicketsChip ticketId={ticketId} variant="link" />}
          />
        )}
      </AiPanel>
      <MajorIncidentBanner ticketId={ticketId} />
      <TicketCisPanel ticket={ticketQ.data} />
      <ProblemPanel ticket={ticketQ.data} />
      <MajorIncidentPanel ticket={ticketQ.data} />
      <TicketApprovalsPanel ticketId={ticketId} />
      <ChangePanel ticket={ticketQ.data} embedApprovals={false} />
      <ProcessWidget
        ticketId={ticketId}
        hideInactiveStart
        allowedActions={allowedActions}
        startOpen={processStartOpen}
        onStartOpenChange={setProcessStartOpen}
      />
      <PluginTicketPanels ticketId={ticketId} />
      <PresenceBar ticketId={ticketId} selfUserId={user?.id} />
      {tab === "articles" ? (
        <ArticleMasterDetail
          ticketId={ticketId}
          onComposingChange={setComposing}
          descending={articleDescending}
          onToggleDescending={() => setArticleDescending((d) => !d)}
          noteOpen={noteOpen}
          onNoteOpenChange={setNoteOpen}
          canNote={canNote}
          canDelete={canDeleteNote}
        />
      ) : (
        <HistoryTable ticketId={ticketId} order={historyOrder} />
      )}
    </div>
  );
}
